/**
 * Player de áudio do WhatsApp: tocar/pausar, barra de progresso clicável, duração e velocidade
 * 1x → 1,5x → 2x (o botão alterna e a escolha vale para os próximos áudios da sessão). O áudio
 * vem de URL assinada de vida curta; se o arquivo não existir (histórico antigo), mostra isso em
 * vez de um player morto.
 */
import React, { useEffect, useRef, useState, type ReactElement } from "react";
import { Pause, Play } from "lucide-react";
import type { ChatMedia } from "../../api/ai-types.js";
import { formatDuration, nextAudioSpeed, speedLabel } from "./helpers.js";

/** Velocidade escolhida fica entre os áudios da sessão (quem acelera um, quer acelerar todos). */
let sessionSpeed = 1;

export function AudioPlayer({ media, label, onTranscribe }: { media: ChatMedia | null; label: string; onTranscribe?: () => Promise<string> }): ReactElement {
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeFailed, setTranscribeFailed] = useState(false);
  // Texto vindo do servidor (já guardado) ou recém-transcrito, antes de a conversa recarregar.
  const [fresh, setFresh] = useState<string | null>(null);
  const transcript = media?.transcript ?? fresh;
  const transcribe = async () => {
    if (!onTranscribe || transcribing) return;
    setTranscribing(true);
    setTranscribeFailed(false);
    try { setFresh(await onTranscribe()); } catch { setTranscribeFailed(true); } finally { setTranscribing(false); }
  };
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState<number>(sessionSpeed);
  const duration = media?.durationSeconds ?? 0;

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  if (!media?.url) {
    return <p className="m-0 text-sm italic text-muted" data-audio="unavailable">{label}: áudio indisponível (arquivo do histórico não foi importado).</p>;
  }

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play().catch(() => setPlaying(false));
    else audio.pause();
  };
  const seek = (event: React.MouseEvent<HTMLButtonElement>) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    const rect = event.currentTarget.getBoundingClientRect();
    audio.currentTime = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)) * audio.duration;
  };
  const cycleSpeed = () => {
    const next = nextAudioSpeed(speed);
    sessionSpeed = next;
    setSpeed(next);
  };
  const shown = playing && audioRef.current ? audioRef.current.currentTime : duration;

  return (
    <div className="flex max-w-sm flex-col gap-1.5" data-audio={media.voiceNote ? "voice-note" : "audio"}>
    <div className="flex min-w-[15rem] max-w-full items-center gap-2">
      <audio
        ref={audioRef}
        src={media.url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onTimeUpdate={(event) => { const a = event.currentTarget; if (a.duration) setProgress(a.currentTime / a.duration); }}
      />
      <button type="button" onClick={toggle} aria-label={playing ? "Pausar áudio" : "Ouvir áudio"} className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-brand text-on-brand">
        {playing ? <Pause className="h-4 w-4" aria-hidden focusable={false} /> : <Play className="h-4 w-4" aria-hidden focusable={false} />}
      </button>
      <button type="button" onClick={seek} aria-label="Ir para um ponto do áudio" className="relative h-2 min-w-[6rem] flex-1 rounded-pill bg-border">
        <span className="absolute inset-y-0 left-0 rounded-pill bg-brand" style={{ width: `${Math.round(progress * 100)}%` }} aria-hidden />
      </button>
      <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted">{formatDuration(shown)}</span>
      <button
        type="button"
        onClick={cycleSpeed}
        aria-label={`Velocidade ${speedLabel(speed)}. Trocar`}
        data-audio-speed={speed}
        className={"min-w-[2.75rem] shrink-0 rounded-pill px-2 py-0.5 text-xs font-extrabold " + (speed > 1 ? "bg-brand text-on-brand" : "bg-surface-soft text-ink")}
      >
        {speedLabel(speed)}
      </button>
    </div>
    {transcript ? (
      <p className="m-0 whitespace-pre-wrap break-words rounded-control bg-surface-soft px-3 py-2 text-sm text-ink" data-audio-transcript>{transcript}</p>
    ) : onTranscribe ? (
      <button type="button" onClick={() => void transcribe()} disabled={transcribing} data-audio-action="transcribe" className="self-start text-xs font-bold text-brand-text underline disabled:opacity-60">
        {transcribing ? "Transcrevendo…" : transcribeFailed ? "Não deu certo. Tentar transcrever de novo" : "Transcrever áudio"}
      </button>
    ) : null}
    </div>
  );
}
